import { ErrorState, StaleBanner } from '../../../components/ui/AsyncState';
/**
 * ShipmentNotifications.tsx
 *
 * On-demand slide-over drawer for shipment notification history.
 *
 * Design constraints (RFC §9, §10):
 *  - Makes ZERO requests until the drawer is explicitly opened.
 *  - Polling starts when opened and stops cleanly when the drawer is closed or unmounted.
 *  - Row expansion lazily fetches the timeline; invalidates only when telemetryVersion changes.
 *  - Read receipts are displayed faithfully — NOT used to derive vendor interest or call action.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useRecoveryQuery as useQuery } from '../../../components/ui/useRecoveryQuery';
import { operationalQueryPolicy } from '../../../api/queryPolicies';
import {
  getShipmentNotificationHistory,
  getShipmentNotificationSummary,
} from '../../../api/endpoints/admin';
import type {
  NotificationHistoryItem,
  NotificationChannel,
  NotificationDeliveryStatus,
  DispatchOutcome,
  HistoryFilter,
} from '../../../api/types';
import {
  notificationLabel,
  channelTheme,
  formatAbsoluteTime,
  formatRelativeTime,
  notificationMilestones,
  calculateHistoryRefetchInterval,
} from './notificationPresentation';
import { NotificationTimeline } from './NotificationTimeline';
import styles from './ShipmentNotifications.module.css';
import { DialogHelp } from '../../../help/DialogHelp';

// ─── KPI Summary Cards ────────────────────────────────────────────────────────

interface SummaryCardsProps {
  shipmentId: number;
  postingCycleFilter: number | null;
  hasActiveTracking: boolean;
}

function SummaryCards({ shipmentId, postingCycleFilter, hasActiveTracking }: SummaryCardsProps) {
  const { t } = useTranslation();
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: [
      'admin', 'notification', 'summary', shipmentId,
      postingCycleFilter,
    ],
    queryFn: () =>
      getShipmentNotificationSummary(shipmentId, {
        postingCycle: postingCycleFilter,
      }),
    ...operationalQueryPolicy,
    refetchInterval: hasActiveTracking ? 60_000 : false,
  });

  if (isError && !data) return <ErrorState title={t('shipments.shipmentNotifications.summaryLoadError')} onRetry={() => refetch()} />;

  const kpis = [
    { key: 'attempted', label: 'Attempted', value: data?.attemptedVendors ?? '—', icon: '📤' },
    { key: 'accepted', label: 'Accepted', value: data?.acceptedVendors ?? '—', icon: '✅' },
    { key: 'delivered', label: 'Delivered', value: data?.deliveredVendors ?? '—', icon: '📬' },
    { key: 'read', label: 'Read', value: data?.readVendors ?? '—', icon: '👁' },
    { key: 'calls', label: 'API Calls', value: data?.providerCalls ?? '—', icon: '🔗' },
    { key: 'failures', label: 'Failures', value: data?.definitiveFailures ?? '—', icon: '❌' },
    { key: 'fallbacks', label: 'Suppressed', value: data?.suppressedFallbacks ?? '—', icon: '⏸' },
  ];

  return (
    <div className={styles.kpiRow}>
      {isError && <StaleBanner onRetry={() => refetch()} />}
      {kpis.map((kpi) => (
        <div key={kpi.key} className={styles.kpiCard}>
          <span className={styles.kpiIcon}>{kpi.icon}</span>
          <span className={styles.kpiValue}>
            {isLoading ? <span className={styles.kpiSkeleton} /> : kpi.value}
          </span>
          <span className={styles.kpiLabel}>{kpi.label}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Milestone Bar ────────────────────────────────────────────────────────────

function MilestoneBar({ item }: { item: NotificationHistoryItem }) {
  const { sent, delivered, read } = notificationMilestones(item);
  const milestones = [
    { label: 'Sent', time: sent },
    { label: 'Delivered', time: delivered },
    { label: 'Read', time: read },
  ];
  return (
    <div className={styles.milestoneBar}>
      {milestones.map((m, idx) => (
        <div key={m.label} className={styles.milestoneStep}>
          <div
            className={`${styles.milestoneDot} ${m.time ? styles.milestoneDotDone : ''}`}
          />
          {idx < milestones.length - 1 && (
            <div
              className={`${styles.milestoneLine} ${milestones[idx + 1].time ? styles.milestoneLineDone : ''}`}
            />
          )}
          <span className={styles.milestoneLabel}>{m.label}</span>
          {m.time && (
            <span className={styles.milestoneTime} title={formatAbsoluteTime(m.time)}>
              {formatRelativeTime(m.time)}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Filter Bar ───────────────────────────────────────────────────────────────

type CycleFilter = 'current' | 'all';

interface FilterBarProps {
  cycleFilter: CycleFilter;
  onCycleFilter: (v: CycleFilter) => void;
  channelFilter: NotificationChannel | '';
  onChannelFilter: (v: NotificationChannel | '') => void;
  statusFilter: NotificationDeliveryStatus | '';
  onStatusFilter: (v: NotificationDeliveryStatus | '') => void;
  outcomeFilter: DispatchOutcome | '';
  onOutcomeFilter: (v: DispatchOutcome | '') => void;
}

function FilterBar({
  cycleFilter, onCycleFilter,
  channelFilter, onChannelFilter,
  statusFilter, onStatusFilter,
  outcomeFilter, onOutcomeFilter,
}: FilterBarProps) {
  const { t } = useTranslation();
  return (
    <div className={styles.filterBar}>
      {/* Posting cycle quick filter */}
      <div className={styles.filterGroup}>
        {(['current', 'all'] as CycleFilter[]).map((v) => (
          <button
            key={v}
            type="button"
            className={`${styles.filterPill} ${cycleFilter === v ? styles.filterPillActive : ''}`}
            onClick={() => onCycleFilter(v)}
          >
            {v === 'current' ? 'Current Cycle' : 'All Cycles'}
          </button>
        ))}
      </div>

      {/* Channel */}
      <select
        className={styles.filterSelect}
        value={channelFilter}
        onChange={(e) => onChannelFilter(e.target.value as NotificationChannel | '')}
      >
        <option value="">{t('shipments.shipmentNotifications.allChannels')}</option>
        <option value="WHATSAPP">{t('shipments.shipmentNotifications.channelWhatsapp')}</option>
        <option value="FCM">{t('shipments.shipmentNotifications.channelFcm')}</option>
        <option value="SMS">{t('shipments.shipmentNotifications.channelSms')}</option>
      </select>

      {/* Status */}
      <select
        className={styles.filterSelect}
        value={statusFilter}
        onChange={(e) => onStatusFilter(e.target.value as NotificationDeliveryStatus | '')}
      >
        <option value="">{t('shipments.shipmentNotifications.allStatuses')}</option>
        <option value="SENT">{t('shipments.shipmentNotifications.statusSent')}</option>
        <option value="DELIVERED">{t('shipments.shipmentNotifications.statusDelivered')}</option>
        <option value="READ">{t('shipments.shipmentNotifications.statusRead')}</option>
        <option value="FAILED">{t('shipments.shipmentNotifications.statusFailed')}</option>
        <option value="CANCELLED">{t('shipments.shipmentNotifications.statusCancelled')}</option>
        <option value="SKIPPED_ALLOWLIST">{t('shipments.shipmentNotifications.statusSkippedAllowlist')}</option>
        <option value="SKIPPED_COOLDOWN">{t('shipments.shipmentNotifications.statusSkippedCooldown')}</option>
        <option value="QUEUED">{t('shipments.shipmentNotifications.statusQueued')}</option>
        <option value="PENDING">{t('shipments.shipmentNotifications.statusPending')}</option>
      </select>

      {/* Dispatch outcome */}
      <select
        className={styles.filterSelect}
        value={outcomeFilter}
        onChange={(e) => onOutcomeFilter(e.target.value as DispatchOutcome | '')}
      >
        <option value="">{t('shipments.shipmentNotifications.allOutcomes')}</option>
        <option value="ACCEPTED">{t('shipments.shipmentNotifications.outcomeAccepted')}</option>
        <option value="DEFINITIVE_FAILURE">{t('shipments.shipmentNotifications.outcomeDefinitiveFailure')}</option>
        <option value="UNKNOWN">{t('shipments.shipmentNotifications.outcomeUnknown')}</option>
        <option value="PRE_PROVIDER_REJECTION">{t('shipments.shipmentNotifications.outcomePreProviderRejection')}</option>
      </select>
    </div>
  );
}

// ─── History Row ──────────────────────────────────────────────────────────────

interface HistoryRowProps {
  item: NotificationHistoryItem;
  expanded: boolean;
  onToggle: () => void;
}

function HistoryRow({ item, expanded, onToggle }: HistoryRowProps) {
  const { t } = useTranslation();
  const { label, color, bgColor } = notificationLabel(item.status, item.dispatchOutcome);
  const ch = channelTheme(item.channel);
  const isFallback =
    item.fallbackOfNotificationId != null ||
    item.childNotificationId != null ||
    item.fallbackFromId != null ||
    item.fallbackToId != null;

  return (
    <>
      <tr
        className={`${styles.historyRow} ${expanded ? styles.historyRowExpanded : ''}`}
        onClick={onToggle}
        aria-expanded={expanded}
      >
        <td className={styles.tdVendor}>
          <div className={styles.vendorName}>{item.vendorDisplayName ?? item.vendorName}</div>
          <div className={styles.vendorPhone}>{item.destinationMasked ?? item.maskedDestination}</div>
        </td>
        <td>
          <span className={styles.channelBadge} style={{ color: ch.color, background: ch.bgColor }}>
            {ch.icon} {ch.label}
          </span>
        </td>
        <td>
          <span className={styles.statusBadge} style={{ color, background: bgColor }}>
            {label}
          </span>
        </td>
        <td className={styles.tdWave}>{t('shipments.shipmentNotifications.wave', { wave: item.waveNumber })}</td>
        <td className={styles.tdTime}>{formatAbsoluteTime(item.scheduledAt)}</td>
        <td className={styles.tdExpandBtn}>
          {isFallback && (
            <span className={styles.fallbackTag} title={t('shipments.shipmentNotifications.fallbackPair')}>
              ⛓
            </span>
          )}
          <span className={styles.expandChevron}>{expanded ? '▲' : '▼'}</span>
        </td>
      </tr>

      {expanded && (
        <tr className={styles.expandedRow}>
          <td colSpan={6} className={styles.expandedCell}>
            <MilestoneBar item={item} />
            {item.failureReason && (
              <div className={styles.failureReason}>
                <strong>{t('shipments.shipmentNotifications.failureReason')}</strong> {item.failureReason}
              </div>
            )}
            <NotificationTimeline item={item} />
          </td>
        </tr>
      )}
    </>
  );
}

// ─── Main Drawer ──────────────────────────────────────────────────────────────

interface ShipmentNotificationsProps {
  shipmentId: number;
  currentPostingCycle: number | null | undefined;
  onClose: () => void;
}

export function ShipmentNotifications({
  shipmentId,
  currentPostingCycle,
  onClose,
}: ShipmentNotificationsProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [cycleFilter, setCycleFilter] = useState<CycleFilter>('current');
  const [channelFilter, setChannelFilter] = useState<NotificationChannel | ''>('');
  const [statusFilter, setStatusFilter] = useState<NotificationDeliveryStatus | ''>('');
  const [outcomeFilter, setOutcomeFilter] = useState<DispatchOutcome | ''>('');
  const [cursor, setCursor] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [accumulatedItems, setAccumulatedItems] = useState<NotificationHistoryItem[]>([]);

  // Ref to track mounted state for polling cleanup
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Dismiss on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  // Reset all state when shipmentId changes
  useEffect(() => {
    setCursor(null);
    setExpandedId(null);
    setAccumulatedItems([]);
    setCycleFilter('current');
    setChannelFilter('');
    setStatusFilter('');
    setOutcomeFilter('');
  }, [shipmentId]);

  // Build filter params
  const filter: HistoryFilter = {
    postingCycle:
      cycleFilter === 'current' ? (currentPostingCycle ?? undefined) : undefined,
    channel: channelFilter || undefined,
    status: statusFilter || undefined,
    dispatchOutcome: outcomeFilter || undefined,
    cursor: cursor ?? undefined,
    limit: 30,
  };

  // Reset cursor and accumulated items whenever filters change
  const filterKey = `${cycleFilter}|${channelFilter}|${statusFilter}|${outcomeFilter}`;
  const prevFilterKeyRef = useRef(filterKey);
  if (prevFilterKeyRef.current !== filterKey) {
    prevFilterKeyRef.current = filterKey;
  }

  const handleFilterChange = useCallback((setter: () => void) => {
    setter();
    setCursor(null);
    setAccumulatedItems([]);
  }, []);

  const { data, isFetching, isError, refetch: refetchHistory } = useQuery({
    queryKey: ['admin', 'notification', 'history', shipmentId, filterKey, cursor],
    queryFn: () => getShipmentNotificationHistory(shipmentId, filter),
    ...operationalQueryPolicy,
    refetchInterval: (query) => {
      const items = query.state.data?.items ?? [];
      return calculateHistoryRefetchInterval(items);
    },
    staleTime: 5_000,
  });

  // Upsert records by notification ID, replacing older versioned evidence (G04)
  useEffect(() => {
    if (!data) return;
    setAccumulatedItems((prev) => {
      const map = new Map<number, NotificationHistoryItem>();
      for (const item of prev) {
        map.set(item.id, item);
      }
      for (const item of data.items) {
        map.set(item.id, item);
      }
      return Array.from(map.values()).sort((a, b) => {
        const timeA = new Date(a.createdAt ?? a.scheduledAt).getTime();
        const timeB = new Date(b.createdAt ?? b.scheduledAt).getTime();
        if (timeB !== timeA) return timeB - timeA;
        return b.id - a.id;
      });
    });
  }, [data]);

  const hasActiveTracking = calculateHistoryRefetchInterval(accumulatedItems) !== false;

  const handleManualRefresh = useCallback(() => {
    refetchHistory();
    queryClient.invalidateQueries({
      queryKey: ['admin', 'notification', 'summary', shipmentId],
    });
  }, [refetchHistory, queryClient, shipmentId]);

  // Stop all notification queries and cancel in-flight requests when unmounted
  useEffect(() => {
    return () => {
      queryClient.cancelQueries({
        queryKey: ['admin', 'notification', 'history', shipmentId],
      });
      queryClient.cancelQueries({
        queryKey: ['admin', 'notification', 'summary', shipmentId],
      });
    };
  }, [queryClient, shipmentId]);

  const hasMore = data?.nextCursor != null;

  return (
    <>
      {/* Backdrop */}
      <div className={styles.backdrop} onClick={onClose} aria-hidden="true" />

      {/* Drawer panel */}
      <aside className={styles.drawer} role="dialog" aria-label={t('shipments.shipmentNotifications.drawerLabel')}>
        {/* Drawer header */}
        <div className={styles.drawerHeader}>
          <div>
            <h2 className={styles.drawerTitle}>{t('shipments.shipmentNotifications.title')}</h2>
            <div className={styles.drawerSubtitle}>{t('shipments.shipmentNotifications.shipmentNumber', { id: shipmentId })}</div>
          </div>
          <div className={styles.headerActions}>
            <DialogHelp context="notification-history" title={t('shipments.shipmentNotifications.title')} />
            <button
              type="button"
              className={styles.refreshBtn}
              onClick={handleManualRefresh}
              disabled={isFetching}
              aria-label={t('shipments.shipmentNotifications.refreshLabel')}
            >
              {t('shipments.shipmentNotifications.refresh')}
            </button>
            <button
              type="button"
              className={styles.closeBtn}
              onClick={onClose}
              aria-label={t('shipments.shipmentNotifications.closeLabel')}
            >
              ✕
            </button>
          </div>
        </div>

        {/* KPI Summary */}
        <SummaryCards
          shipmentId={shipmentId}
          postingCycleFilter={
            cycleFilter === 'current' ? (currentPostingCycle ?? null) : null
          }
          hasActiveTracking={hasActiveTracking}
        />

        {/* Filters */}
        <FilterBar
          cycleFilter={cycleFilter}
          onCycleFilter={(v) => handleFilterChange(() => setCycleFilter(v))}
          channelFilter={channelFilter}
          onChannelFilter={(v) => handleFilterChange(() => setChannelFilter(v))}
          statusFilter={statusFilter}
          onStatusFilter={(v) => handleFilterChange(() => setStatusFilter(v))}
          outcomeFilter={outcomeFilter}
          onOutcomeFilter={(v) => handleFilterChange(() => setOutcomeFilter(v))}
        />

        {/* Table */}
        <div className={styles.tableWrap}>
          {isError && (
            <div className={styles.errorMsg}>{t('shipments.shipmentNotifications.loadError')}</div>
          )}
          {!isError && accumulatedItems.length === 0 && !isFetching && (
            <div className={styles.emptyMsg}>{t('shipments.shipmentNotifications.empty')}</div>
          )}
          {accumulatedItems.length > 0 && (
            <table className={styles.historyTable}>
              <thead>
                <tr>
                  <th>{t('shipments.shipmentNotifications.colVendor')}</th>
                  <th>{t('shipments.shipmentNotifications.colChannel')}</th>
                  <th>{t('shipments.shipmentNotifications.colStatus')}</th>
                  <th>{t('shipments.shipmentNotifications.colWave')}</th>
                  <th>{t('shipments.shipmentNotifications.colScheduled')}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {accumulatedItems.map((item) => (
                  <HistoryRow
                    key={item.id}
                    item={item}
                    expanded={expandedId === item.id}
                    onToggle={() =>
                      setExpandedId((prev) => (prev === item.id ? null : item.id))
                    }
                  />
                ))}
              </tbody>
            </table>
          )}

          {isFetching && (
            <div className={styles.loadingMsg}>
              <span className={styles.spinner} /> {t('shipments.shipmentNotifications.loading')}
            </div>
          )}

          {hasMore && !isFetching && (
            <button
              type="button"
              className={styles.loadMoreBtn}
              onClick={() => setCursor(data!.nextCursor)}
            >
              {t('shipments.shipmentNotifications.loadMore')}
            </button>
          )}
        </div>

        <div className={styles.drawerFooter}>
          {data?.asOf && (
            <span className={styles.asOf}>
              {t('shipments.shipmentNotifications.dataAsOf', { time: formatAbsoluteTime(data.asOf) })}
            </span>
          )}
        </div>
      </aside>
    </>
  );
}
